import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import ItemGroup from "@shared/components/application/data-display/item-group/ItemGroup.web";
import { DELIVERY_TYPE_OPTIONS, enums } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import useUpdateEffect from "@shared/hooks/useUpdateEffect/useUpdateEffect";
import useCheckoutDeliveryMethods from "@shared/modules/checkout/hooks/useCheckoutDeliveryMethods";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import CheckoutBaseLayoutWithCart from "@app-contexts/public/shared/checkout/templates/CheckoutBaseLayoutWithCart/CheckoutBaseLayoutWithCart.web";
import ApplicableTicketCredits from "@app-contexts/public/shared/checkout/components/ApplicableTicketCredits/ApplicableTicketCredits.web";
import { ContinueButton } from "@app-contexts/public/shared/checkout/components/ContinueButton/ContinueButton.web";
import CheckoutSummary from "@app-contexts/public/shared/checkout/components/CheckoutSummary/CheckoutSummary.web";
import AllowedShippingWarning from "@app-contexts/public/shared/checkout/components/AllowedShippingWarning/AllowedShippingWarning.web";
import PublicCheckoutDeliverySingleOption from "./delivery-methods/PublicCheckoutDeliverySingleOption/PublicCheckoutDeliverySingleOption.web";
import PublicCheckoutDeliveryWithSelectionRequirement from "./delivery-methods/PublicCheckoutDeliveryWithSelectionRequirement/PublicCheckoutDeliveryWithSelectionRequirement.web";
import type { ReviewSharedProps } from "./Review.props";
import ReviewItemGroup from "./components/ReviewItemGroup/ReviewItemGroup.web";

export default function Review({
  onSuccess,
  onError,
  hideContinueButton = false,
}: {
  onSuccess: () => void;
  onError: () => void;
  hideContinueButton?: boolean;
}): JSX.Element {
  const { t } = useTranslation("common");
  const { itemGroups, paymentVenue, holdLinkSlug } = usePublicBasket();

  const { useRetrieve } = usePublicVenueService();
  const { data: venue } = useRetrieve(paymentVenue, {
    options: {
      enabled: Boolean(paymentVenue),
    },
  });

  const shouldAllowQuantityChange =
    Boolean(venue?.enable_customer_basket_extend) && !holdLinkSlug;

  if (!itemGroups?.length) {
    return <></>;
  }

  return (
    <SeatingStateProvider
      isOnSeatingMapPage={false}
      basketType={DataProvider.Public} // used to retrieve the basket from store in the seating provider
    >
      <CheckoutBaseLayoutWithCart
        rightColumn={
          <CheckoutSummary
            callToActionButton={hideContinueButton ? null : <ContinueButton />}
          />
        }
      >
        <Flex p={{ base: "lg", sm: "xl" }} gap="md" direction="column" bg="bg">
          <ApplicableTicketCredits />
          <Text
            fontSize="xl"
            fontWeight="bold"
            data-testid="review-page-heading"
            display={{
              base: "none",
              sm: "block",
            }}
          >
            {t("Review")}
          </Text>
          <Flex direction="column" gap="2xl">
            <Review.ItemGroupsByDelivery
              onSuccess={onSuccess}
              onError={onError}
              allowQuantityChange={shouldAllowQuantityChange}
            />
          </Flex>
          <Review.ItemGroupsUnsorted
            allowQuantityChange={shouldAllowQuantityChange}
          />
        </Flex>
      </CheckoutBaseLayoutWithCart>
    </SeatingStateProvider>
  );
}

export const ticketDeliveryTypes = [DELIVERY_TYPE_OPTIONS.TYPE_EMAIL] as const;

/**
 * Groups item groups of type tickets and products based on their shipping types.
 *
 */
Review.ItemGroupsByDelivery = function ItemGroupsByDelivery({
  onSuccess,
  onError,
  allowQuantityChange = true,
}: {
  onSuccess: () => void;
  onError: () => void;
  allowQuantityChange: boolean;
}): JSX.Element {
  const publicBasket = usePublicBasket();

  const { useRetrieve } = usePublicVenueService();
  const { data: venue, isLoading: isVenueLoading } = useRetrieve(
    publicBasket.paymentVenue,
    {
      options: {
        enabled: Boolean(publicBasket.paymentVenue),
      },
    }
  );

  const [deliveryMethodSelectionRequired, setDeliveryMethodSelectionRequired] =
    useState(true);

  const {
    isLoading,
    shippingTypePrintItemGroups,
    shippingTypeWillCallItemGroups,
    shippingTypeDeliveryItemGroups,
    mixedShippingTypesItemGroups,
    isShippingAddressRequired,
    setDeliveryMethod,
    selectedDeliveryMethod,
    isDeliveryMethodUpdating,
    shippingIsAllowed,
  } = useCheckoutDeliveryMethods({
    basket: publicBasket,
    useEventService: usePublicEventsService,
    useMembershipService: usePublicMembershipGroupsService,
    venue,
    onSetDeliveryMethod(_, selectedMethods, mixedShippingTypeItemGroups) {
      const isDeliveryMethodSelectionRequired =
        Object.keys(selectedMethods).length !==
        Object.keys(mixedShippingTypeItemGroups).length;

      setDeliveryMethodSelectionRequired(isDeliveryMethodSelectionRequired);
    },
    productServiceType: DataProvider.Public,
  });

  // On public checkout, the only delivery method selection required comes from item groups
  // that have delivery methods other than st_print (print/email).
  // These item groups are part of mixedShippingTypesItemGroups.
  // If this is empty, assume that everything is st_print and we can automatically call onSuccess.
  // This check only needs to happen once.
  const hasCheckedForMixedShippingTypes = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasCheckedForMixedShippingTypes.current) {
      if (mixedShippingTypesItemGroups.length === 0) {
        hasCheckedForMixedShippingTypes.current = true;
        setDeliveryMethodSelectionRequired(false);
      }
    }
  }, [isLoading, mixedShippingTypesItemGroups]);

  useUpdateEffect(() => {
    if (deliveryMethodSelectionRequired) {
      onError();
    } else {
      onSuccess();
    }
  }, [deliveryMethodSelectionRequired, isShippingAddressRequired]);

  const limitedShippingCountries = useMemo(
    () =>
      venue?.allowed_shipping_countries?.length > 0
        ? venue?.allowed_shipping_countries
        : null, // null if all countries are allowed
    [venue?.allowed_shipping_countries]
  );

  if (isLoading || isVenueLoading) {
    return <ItemGroup.Skeleton />;
  }

  return (
    <>
      {limitedShippingCountries && shippingIsAllowed && (
        <AllowedShippingWarning allowedCountries={limitedShippingCountries} />
      )}
      {mixedShippingTypesItemGroups.length > 0 && (
        <PublicCheckoutDeliveryWithSelectionRequirement
          mixedShippingTypesItemGroups={mixedShippingTypesItemGroups}
          selectedDeliveryMethod={selectedDeliveryMethod}
          isDeliveryMethodUpdating={isDeliveryMethodUpdating}
          onSelectMethod={setDeliveryMethod}
          availableTicketDeliveryTypes={ticketDeliveryTypes}
          allowQuantityChange={allowQuantityChange}
        />
      )}

      {/* Differs from box-office as Print shipping types do not require selection from the public */}
      {(shippingTypePrintItemGroups.length > 0 ||
        shippingTypeWillCallItemGroups.length > 0 ||
        shippingTypeDeliveryItemGroups.length > 0) && (
        <PublicCheckoutDeliverySingleOption
          shippingTypePrintItemGroups={shippingTypePrintItemGroups}
          shippingTypeWillCallItemGroups={shippingTypeWillCallItemGroups}
          shippingTypeDeliveryItemGroups={shippingTypeDeliveryItemGroups}
          allowQuantityChange={allowQuantityChange}
        />
      )}
    </>
  );
};

Review.ItemGroupsUnsorted = function ItemGroupsUnsorted({
  allowQuantityChange,
}: {
  allowQuantityChange: boolean;
}) {
  const { itemGroups } = usePublicBasket();

  return (
    <>
      {/*
       * With the other display being sorted by delivery,
       * Item groups that are not tickets and products and memberships are ineligible to have delivery method selection
       * which should be displayed here
       */}
      {itemGroups
        .filter(
          (itemGroup) =>
            itemGroup.item_type !== enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id &&
            itemGroup.item_type !== enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id &&
            itemGroup.item_type !==
              enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore -- cached build problems
            itemGroup.item_type !== "protection"
        )
        .map((itemGroup) => (
          <ReviewItemGroup
            key={itemGroup.id}
            itemGroup={itemGroup}
            allowQuantityChange={allowQuantityChange}
          />
        ))}
    </>
  );
};

export type ReviewProps = ReviewSharedProps & {
  // Add any web specific props here.
};
